import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Target } from 'lucide-react';
import {
  BaseDetailCard,
  MetrcLogo,
  NEW,
  cardClasses,
  cropClasses,
  endpoints,
  errFeedbackFn,
  fadeTimingMs,
  logMsgToConsole,
  metricPlanCardClasses,
  LoadingSpinner,
} from '../../../utility';
import { Fade, Typography, Box, Paper, Tabs, Tab, Stack, Tooltip, Button } from '@mui/material';
import EntityDetailTimeline from '../../utils/EntityDetailTimeline';
import APIService from '../../../../api/apiService';
import NoEntityMsg from '../../../utility/messages/NoEntityMsg';
import RoutinesPopup from '../../Popup/RoutinesPopup';
import { FRTLContext } from '../../../../context/FRTLContext';
import AttachNewRoutinePopup from '../../Popup/AttachNewRoutinePopup';
import AttachPhasePlanPopup from '../../Popup/AttachPhasePlanPopup';
import { fetchFrtlData } from '../../../../api/apiUtils';

const subHeaderkeys = [
  { label: 'Total Tasks:', value: 'tasks_count' },
  { label: 'Pending:', value: 'tasks_status' },
];

const COMPLETESTATUSKEY = 'complete';
const METRC_VALIDATED = 'metrc_validated';
const today = new Date();
today.setHours(0, 0, 0, 0);

export default function _PhasesTab(props) {
  const {
    currDarkThemeClass,
    cropplanid,
    selectedCropRunFarm,
    values: parentValues = {},
    handleErrorWithFeedback,
    cropPlanPerms,
    handleSetParentValues,
  } = props;

  const [values, setValues] = useState({
    strain: [],
    farm: null,
    selectedStrain: 0,
    selectedPhase: 0,
    attachRoutinePopupOpen: false,
    attachPhasePlanPopupOpen: false,
    selectedRoutine: null,
    selectedSubphase: null,
  });
  const handleSetValues = useCallback((prop, value) => setValues(state => ({ ...state, [prop]: value })), []);
  const [detailChildren, setDetailChildren] = useState(null);
  const { frtlContextValue, setFrtlContextValue } = useContext(FRTLContext);

  useEffect(() => {
    if (!frtlContextValue) {
      fetchFrtlData()
        .then(data => {
          setFrtlContextValue(data || {});
        })
        .catch(error => {
          console.error('Error loading FRTL data:', error);
          setFrtlContextValue({});
        });
    }
  }, [frtlContextValue, setFrtlContextValue]);

  useEffect(() => {
    if (values.strain.length > 0) {
      const cropRunFarm = values.strain[0]?.farm_id;
      handleSetValues('farm', cropRunFarm);
    }
  }, [values.strain]);

  const pathwayTitle = useMemo(
    () =>
      `${values.strain[values.selectedStrain]?.phases[values.selectedPhase]?.name ?? ''} Pathway for ( ${values.strain[
        values.selectedStrain
      ]?.batches_names?.join(', ')} )`,
    [values?.selectedPhase, values?.selectedStrain, values?.strain]
  );

  const getLevelCompletedBySubphase = useCallback(subphase => {
    // Return early if the subphase has no routines
    const routines = subphase.routines || [];
    if (!routines.length) return false;

    // Use Array.every() and Array.some() to short-circuit the iteration
    return routines.every(routine => {
      const routineTasks = routine.routine_tasks || [];
      // If there are no routine tasks, consider the routine incomplete
      if (!routineTasks.length) return false;

      // Use Array.some() to check if any task is not complete
      return !routineTasks.some(task => task.status !== COMPLETESTATUSKEY && task.status !== METRC_VALIDATED);
    });
  }, []);

  const handleSetPhaseRes = res => {
    handleSetValues('loading', false);

    const { data } = res;

    setValues(state => ({
      ...state,
      strain: data,
    }));
  };

  // const is_auto = values?.strain[0]?.phases[0]?.is_auto;

  useEffect(() => {
    handleSetParentValues('defStrainWithDefRoutines', values?.strain);
  }, [values?.strain]);

  const fetchPhasesData = () => {
    let entity = '',
      errConfig = {};

    handleSetValues('loading', true);
    if (handleSetParentValues) handleSetParentValues('isPhaseDataLoading', true);

    if (cropplanid === NEW) {
      const { farmstrains, phaseplans, farms, units_of_measure, assignees, defStrainWithDefRoutines, qualityplans, users, ...reviewRoutinesValues } =
        parentValues || {};

      entity = `v2/review-cropplan-routines`;

      APIService.createInstance(entity, reviewRoutinesValues)
        .then(handleSetPhaseRes)
        .catch(handleErrorWithFeedback(errFeedbackFn(errConfig)))
        .finally(() => {
          handleSetValues('loading', false);
          if (handleSetParentValues) handleSetParentValues('isPhaseDataLoading', false);
        });
    } else {
      entity = `v2/cropplan/phases/?id=${cropplanid}`;

      APIService.fetchInstance(entity)
        .then(handleSetPhaseRes)
        .catch(handleErrorWithFeedback(errFeedbackFn(errConfig)))
        .finally(() => {
          handleSetValues('loading', false);
          if (handleSetParentValues) handleSetParentValues('isPhaseDataLoading', false);
        });
    }
  };

  useEffect(() => {
    fetchPhasesData();
  }, [currDarkThemeClass]);

  const handleStrainChange = (event, newValue) => {
    handleSetValues('selectedStrain', newValue);
    handleSetValues('selectedPhase', 0);
  };

  const handlePhaseChange = (event, newValue) => {
    handleSetValues('selectedPhase', newValue);
  };

  // Helper function to check if a subphase is the target subphase
  const isTargetSubphase = useCallback(
    subphase => {
      const currentPhase = values?.strain?.[values?.selectedStrain]?.phases?.[values?.selectedPhase];

      // Get target subphase ID from target_details
      const targetSubphaseId = currentPhase?.target_details?.target_subphase_id;

      return targetSubphaseId && subphase.default_subphase_id === targetSubphaseId;
    },
    [values?.strain, values?.selectedStrain, values?.selectedPhase]
  );

  const pTPhProps = {
    variant: 'square',
    elevation: 0,
    sx: {
      background: 'transparent',
      overflow: 'auto',
    },
  };

  const handleRoutineModifyRes = (res, subphase_id) => {
    const { data } = res;
    const routine_id = data?.id;

    handleSetValues(
      'strain',
      values?.strain?.map((strain, i) =>
        values?.selectedStrain === i
          ? {
            ...strain,
            phases: strain?.phases?.map((phase, j) =>
              values?.selectedPhase === j
                ? {
                  ...phase,
                  subphases: phase?.subphases?.map(subphase =>
                    subphase?.id === subphase_id
                      ? {
                        ...subphase,
                        routines: subphase?.routines?.map(routine => (routine?.id === routine_id ? data : routine)),
                      }
                      : subphase
                  ),
                }
                : phase
            ),
          }
          : strain
      )
    );
  };

  const handleRoutinesPopupSave = useCallback(
    (subphase_id, routine_id) => async value => {
      if (cropplanid == NEW) {
        handleSetValues(
          'strain',
          values?.strain?.map((strain, i) =>
            values?.selectedStrain === i
              ? {
                ...strain,
                phases: strain?.phases?.map((phase, j) =>
                  values?.selectedPhase === j
                    ? {
                      ...phase,
                      subphases: phase?.subphases?.map(subphase =>
                        subphase?.id === subphase_id
                          ? {
                            ...subphase,
                            routines: subphase?.routines?.map(routine =>
                              (routine?.id === routine_id ? value : routine)
                            ),
                          }
                          : subphase
                      ),
                    }
                    : phase
                ),
              }
              : strain
          )
        );
        handlePopupClose();
        return;
      }

      let errConfig = {};
      const entityWithID = endpoints.routine.detail(routine_id);
      let res = {};

      try {
        res = await APIService.modifyInstance(entityWithID, value);
        handleRoutineModifyRes(res, subphase_id);
      } catch (err) {
        handleErrorWithFeedback(errFeedbackFn(errConfig))(err);
      }

      handlePopupClose();
    },
    [values, cropplanid]
  );

  const handleSeeMoreRoutines = (routine, subphase) => () => {
    if (!cropPlanPerms?.read) return;
    handleSetValues('routinesOpen', true);

    setDetailChildren({
      routine,
      cropplanid,
      subphase,
      assignees: parentValues?.assignees,
      users: parentValues?.users,
      owner: parentValues?.owner,
      handleRoutinesPopupSave: handleRoutinesPopupSave(subphase?.id, routine?.id),
    });
  };

  const handleAttachNewRoutinePopup = subphase => {
    if (!cropPlanPerms?.create) return;
    setValues(state => {
      return {
        ...state,
        default_routine: '',
        attachRoutinePopupOpen: true,
        selectedSubphase: subphase,
      };
    });
  };

  const routineActionBtns = (routine, subphase) => [
    {
      key: 'see details',
      title: 'See Details',
      icon: 'div',
      otherProps: {
        onClick: handleSeeMoreRoutines(routine, subphase),
      },
    },
  ];

  const pTSubHeaderTypo = {
    className: `${metricPlanCardClasses.subHeaderTypo} ${currDarkThemeClass}`,
    variant: 'subtitle2',
  };

  const routineSubHeaders = useCallback(routine => {
    // Calculate pending tasks count
    const pendingTasksCount =
      routine?.routine_tasks?.filter(task => task.status !== COMPLETESTATUSKEY && task.status !== METRC_VALIDATED).length || 0;

    return (
      <Stack direction="row" width="100%" gap={2}>
        {subHeaderkeys.map((subheader, i) => (
          <Typography {...pTSubHeaderTypo} key={i}>
            {`${subheader.label} ${subheader.value === 'tasks_count' ? routine?.tasks_count ?? 0 : subheader.value === 'tasks_status' ? pendingTasksCount : 0
              }`}
          </Typography>
        ))}
      </Stack>
    );
  }, []);

  const pTRoutineCardProps = useCallback(
    (key, routine, subphase) => ({
      key,
      currDarkThemeClass,
      baseEntity: (
        <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
          Routine {+key + 1}: {routine?.name}{' '}
          {routine?.routine_tasks?.length && routine?.routine_tasks[0].isMetrc ? <MetrcLogo style={{ width: 24, height: 24, marginLeft: 8 }} /> : ''}
        </div>
      ),
      isHeaderClickable: true,
      headerClickHandler: handleSeeMoreRoutines(routine, subphase),
      NoEntityMsg: 'Unknown Routine',
      cardActionBtns: routineActionBtns(routine, subphase),
      subHeader: routineSubHeaders(routine),
    }),
    [values]
  );

  const routinesList = useCallback(
    subphase => {
      let list = subphase?.routines?.map((routine, i) => <BaseDetailCard {...pTRoutineCardProps(i, routine, subphase)} />);

      if (!list.length) {
        list.push(<NoEntityMsg isLogo={false} isHeadContent={false} msg="No Available Routines" />);
      }

      return list;
    },
    [values]
  );

  const getSubTitle = subphase => {
    const currentPhase = values?.strain?.[values?.selectedStrain]?.phases?.[values?.selectedPhase];
    const allSubphases = currentPhase?.subphases || [];
    const targetSubphaseId = currentPhase?.target_details?.target_subphase_id;

    // Find the target subphase
    const targetSubphase = allSubphases.find(sp => sp.default_subphase_id === targetSubphaseId);

    let startDate = new Date(subphase?.start_date).toLocaleDateString(undefined, {
      year: '2-digit',
      month: 'short',
      day: 'numeric',
    });

    let endDate = new Date(subphase?.end_date).toLocaleDateString(undefined, {
      year: '2-digit',
      month: 'short',
      day: 'numeric',
    });

    let dayDisplay = '';
    let tooltipText = '';

    if (targetSubphase) {
      // Calculate day difference from target start date
      const targetStartDate = new Date(targetSubphase?.start_date);
      const currentStartDate = new Date(subphase?.start_date);

      // Calculate the difference in days
      const dayDifference = Math.floor((currentStartDate - targetStartDate) / (1000 * 60 * 60 * 24));

      // If this is the target subphase itself (check by ID)
      if (subphase.default_subphase_id === targetSubphaseId) {
        dayDisplay = `(+1 target day)`;
      } else {
        // For other subphases, calculate based on difference
        if (dayDifference < 0) {
          dayDisplay = `(${dayDifference})`;
          tooltipText = `Days from target: ${dayDifference}`;
        } else {
          const daysFromTarget = dayDifference + 1;
          dayDisplay = `(+${daysFromTarget})`;
          tooltipText = `Days from target: +${daysFromTarget}`;
        }
      }
    } else {
      // Fallback
      const duration = subphase?.end_day - subphase?.start_day + 1;
      dayDisplay = `(${duration})`;
      tooltipText = `Duration: ${duration} days`;
    }

    return (
      <Typography>
        {`${startDate} - ${endDate} `}
        <Tooltip title={tooltipText} arrow placement="top">
          <span>{dayDisplay}</span>
        </Tooltip>
      </Typography>
    );
  };
  const getDefaultExpandState = subphase => {
    let startDate = new Date(subphase?.start_date);
    let endDate = new Date(subphase?.end_date);

    startDate.setHours(0, 0, 0, 0);
    endDate.setHours(0, 0, 0, 0);

    return startDate <= today && endDate >= today;
  };

  const timeLineEntry = useMemo(() => {
    const subphases = values?.strain?.[values?.selectedStrain]?.phases?.[values?.selectedPhase]?.subphases || [];
    const currentPhase = values?.strain?.[values?.selectedStrain]?.phases?.[values?.selectedPhase];

    let timelineEntries =
      subphases?.map(subphase => {
        const isTarget = isTargetSubphase(subphase);
        return {
          key: (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span>{subphase.name}</span>
              {isTarget && (
                <Tooltip title="Target" arrow placement="top">
                  <div style={{ display: 'flex' }}>
                    <Target
                      size={20}
                      style={{
                        color: '#B1D23A',
                        flexShrink: 0,
                      }}
                    />
                  </div>
                </Tooltip>
              )}
            </div>
          ),
          children: routinesList(subphase),
          withSubtitle: true,
          subtitle: getSubTitle(subphase),
          isLevelCompleted: getLevelCompletedBySubphase(subphase),
          defaultExpanded: getDefaultExpandState(subphase),
          subphase: subphase,
        };
      }) ?? [];

    const entityDetialTimelineProps = {
      currDarkThemeClass: currDarkThemeClass,
      timelineEntries: timelineEntries,
      showAttachRoutineButton: !!cropPlanPerms?.update,
      onAttachRoutine: handleAttachNewRoutinePopup,
      subphases: subphases,
    };

    return <EntityDetailTimeline {...entityDetialTimelineProps} />;
  }, [values?.strain, values?.selectedPhase, values?.selectedStrain, isTargetSubphase]);

  const pTBoxProps = {
    className: `${cropClasses?.phases?.phasesBox} ${currDarkThemeClass}`,
  };

  const dICardTitleTypoProps = {
    className: `${cardClasses?.detailInfo?.titleTypo} ${currDarkThemeClass}`,
    sx: { marginTop: '10px' },
  };

  const strainTabProps = {
    className: `${cropClasses?.phases?.strainTab} ${currDarkThemeClass}`,
  };

  const strainTabsProps = {
    className: `${cropClasses?.phases?.strainTab} ${currDarkThemeClass}`,
    value: values.selectedStrain,
    onChange: handleStrainChange,
  };

  const strainTabList = values?.strain?.map((strain, index) => <Tab key={index} label={strain?.strain_name} {...strainTabProps} />);

  const pTPaperProps = {
    square: true,
    variant: 'none',
    className: `${cropClasses?.phases?.strainContainer} ${currDarkThemeClass}`,
  };

  const phaseTabProps = {
    className: `${cropClasses?.phases?.phaseTab} ${currDarkThemeClass}`,
  };

  const phaseTabsProps = {
    className: `${cropClasses?.phases?.phaseTab} ${currDarkThemeClass}`,
    value: values.selectedPhase,
    onChange: handlePhaseChange,
  };

  const phasesTabsList = values?.strain?.[values?.selectedStrain]?.phases?.map((phase, i) => (
    <Tab key={i} label={phase?.name ?? ''} {...phaseTabProps} />
  ));

  const handlePopupClose = () => {
    setDetailChildren({});
    if (cropplanid !== NEW) {
      fetchPhasesData();
    }
    handleSetValues('routinesOpen', false);
  };

  const handleOpenAttachPhasePlan = () => {
    handleSetValues('attachPhasePlanPopupOpen', true);
  };

  const handleCloseAttachPhasePlan = () => {
    handleSetValues('attachPhasePlanPopupOpen', false);
  };

  const handleAttachPhasePlanSuccess = () => {
    fetchPhasesData();
  };

  const selectedFarm = values?.farm;

  const pTPopupProps = {
    currDarkThemeClass,
    open: values?.routinesOpen,
    cropPlanPerms: cropPlanPerms,
    onClose: handlePopupClose,
    onSaveClick: handleRoutinesPopupSave(),
    selectedCropRunFarm: selectedFarm,
    ...detailChildren,
  };

  if (logMsgToConsole?.cropPlansTabs?.phases) {
    console.log(props);
    console.log(values);
  }

  const pTAttachRoutinePopupProps = {
    currDarkThemeClass,
    fetchPhasesData,
    open: values?.attachRoutinePopupOpen,
    parentValues: values?.strain[0],
    farm: selectedFarm,
    selectedSubphase: values?.selectedSubphase,
    onClose: () => {
      setValues(state => ({
        ...state,
        attachRoutinePopupOpen: false,
        selectedRoutine: null,
        selectedSubphase: null,
        default_routine: '',
      }));
    },
  };

  const pTAttachPhasePlanBtnProps = {
    variant: 'contained',
    onClick: handleOpenAttachPhasePlan,
    sx: {
      background: 'linear-gradient(135deg, #B1D23A 0%, #7EA82A 100%)',
      color: '#000',
      fontWeight: 700,
      mt: 2,
      '&:hover': { background: 'linear-gradient(135deg, #C5E44A 0%, #8EBB30 100%)' },
    },
  };

  const attachPhasePlanPopupProps = {
    open: values?.attachPhasePlanPopupOpen,
    onClose: handleCloseAttachPhasePlan,
    cropplanid,
    currDarkThemeClass,
    phaseplans: parentValues?.phaseplans ?? [],
    onSuccess: handleAttachPhasePlanSuccess,
  };

  const hasNoPhaseData =
    !values?.loading &&
    (!values?.strain ||
      values?.strain?.length === 0 ||
      values?.strain.every(s => !s.phases || s.phases.length === 0));

  return (
    <Fade in={true} timeout={fadeTimingMs}>
      <Box {...pTBoxProps}>
        {values?.loading ? (
          <LoadingSpinner
            message="Loading Phases Data"
            currDarkThemeClass={currDarkThemeClass}
            className={`${cardClasses?.detailInfo?.titleTypo} ${currDarkThemeClass}`}
          />
        ) : (
          <>
            <Tabs {...strainTabsProps}>{strainTabList}</Tabs>
            <Paper {...pTPaperProps}>
              <Tabs {...phaseTabsProps}>{phasesTabsList}</Tabs>
              <Typography {...dICardTitleTypoProps}>{pathwayTitle}</Typography>
              <Paper {...pTPhProps}>
                {hasNoPhaseData ? (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      py: 8,
                      gap: 2,
                    }}
                  >
                    <Typography
                      sx={{
                        color: 'rgba(255,255,255,0.5)',
                        fontSize: '1rem',
                        textAlign: 'center',
                      }}
                    >
                      No Phase Plan is attached to this Crop Run.
                    </Typography>
                    {cropplanid !== NEW && cropPlanPerms?.update && (
                      <Button {...pTAttachPhasePlanBtnProps}>Attach Phase Plan</Button>
                    )}
                  </Box>
                ) : (
                  timeLineEntry
                )}
              </Paper>
            </Paper>
          </>
        )}
        {values?.routinesOpen && <RoutinesPopup {...pTPopupProps} />}
        <AttachNewRoutinePopup {...pTAttachRoutinePopupProps} />
        <AttachPhasePlanPopup {...attachPhasePlanPopupProps} />
      </Box>
    </Fade>
  );
}
